import type {
  KYCQuestionDefinition,
  KYCQuestionnaire,
  KYCQuestionnaireElement,
} from '@anchorage/graphql/generated';
import type { reactHookForm } from '@latitude/form';
import { yupResolver } from '@latitude/form';

import {
  getElementQuestions,
  getHiddenQuestionIDs,
  questionIDsByAnsweredOptions,
} from '../hooks/useQuestionnaire/utils';
import type { QuestionnaireValidationContext } from './yupValidation';
import {
  createFormSchemaFromQuestionnaire,
  getQuestionnaireBody,
} from './yupValidation';

/**
 * Builds the yup context for one validation run from the current form values:
 * which options are answered and, from those, which questions are hidden.
 */
export const getQuestionnaireValidationContext = ({
  body,
  values,
  allQuestions,
}: {
  body: KYCQuestionnaireElement;
  values: reactHookForm.FieldValues;
  allQuestions?: KYCQuestionDefinition[];
}): QuestionnaireValidationContext => {
  const answeredOptions = questionIDsByAnsweredOptions(values);

  return {
    answeredOptions,
    hiddenQuestionIDs: getHiddenQuestionIDs({
      body,
      answeredOptions,
      allQuestions,
    }),
  };
};

/**
 * react-hook-form resolver for a KYC questionnaire form.
 *
 * The yup schema is built once. The visibility context is derived from the
 * form values on every run, so questions hidden by unmet triggers are skipped
 * by yup itself, without any React state or unregistering.
 */
export const createQuestionnaireResolver = (
  questionnaire: KYCQuestionnaire | KYCQuestionnaireElement,
): reactHookForm.Resolver<reactHookForm.FieldValues> => {
  const body = getQuestionnaireBody(questionnaire);
  const resolveWithSchema = yupResolver.yupResolver(
    createFormSchemaFromQuestionnaire(questionnaire),
  );
  const allQuestions = body ? getElementQuestions(body) : [];

  // react-hook-form passes the `context` option of `useForm` as the second
  // argument. This resolver ignores it. It always builds the yup context from
  // the current form values, so the schema sees the current visibility.
  return (values, _context, options) =>
    resolveWithSchema(
      values,
      body
        ? getQuestionnaireValidationContext({ body, values, allQuestions })
        : undefined,
      options,
    );
};
